<template>
    <div class="jdoc-browse">
        <!-- 移动端：导航折叠开关（桌面端隐藏，左侧导航常驻） -->
        <button type="button" class="jdoc-browse__toggle" @click="navOpen = !navOpen">
            <el-icon><Operation /></el-icon>
            <span class="jdoc-browse__toggletext">{{ currentPathText }}</span>
            <el-icon>
                <ArrowDown v-if="!navOpen" />
                <ArrowUp v-else />
            </el-icon>
        </button>

        <!-- 左侧：工程 / 包 / 类 三级导航 -->
        <aside class="jdoc-browse__nav jdoc-panel" :class="{ 'is-open': navOpen }">
            <div class="jdoc-browse__group">
                <div class="jdoc-browse__grouptitle">
                    <el-icon><OfficeBuilding /></el-icon>
                    <span>工程</span>
                </div>
                <div class="jdoc-browse__list">
                    <button v-for="item in projectList" :key="item.id" type="button" class="jdoc-item" :class="{ 'is-active': item.id === viewData.currentProjectId }" :title="item.name" @click="getPackageList(item.id, item.version)">
                        {{ item.name }}
                    </button>
                    <el-empty v-if="projectList.length == 0" description="暂无工程" :image-size="48" />
                </div>
            </div>

            <div class="jdoc-browse__group">
                <div class="jdoc-browse__grouptitle">
                    <el-icon><Files /></el-icon>
                    <span>包</span>
                </div>
                <div class="jdoc-browse__list">
                    <button v-for="item in viewData.packageList" :key="item" type="button" class="jdoc-item" :class="{ 'is-active': item === viewData.currentPackageName }" :title="item" @click="getClassList(item)">
                        {{ item }}
                    </button>
                    <el-empty v-if="viewData.packageList.length == 0" :description="viewData.currentProjectId ? '该工程暂无包' : '请先选择工程'" :image-size="48" />
                </div>
            </div>

            <div class="jdoc-browse__group">
                <div class="jdoc-browse__grouptitle">
                    <el-icon><Collection /></el-icon>
                    <span>类</span>
                </div>
                <div class="jdoc-browse__list">
                    <button v-for="item in viewData.classList" :key="item.id" type="button" class="jdoc-item" :class="{ 'is-active': item.id === viewData.currentClassId }" :title="item.name" @click="getClassDetail(item.id)">
                        {{ item.name }}
                    </button>
                    <el-empty v-if="viewData.classList.length == 0" :description="viewData.currentPackageName ? '该包下暂无类' : '请先选择包'" :image-size="48" />
                </div>
            </div>
        </aside>

        <!-- 右侧：类与方法的注释卡片 -->
        <section class="jdoc-browse__main">
            <template v-if="viewData.methodList.length > 0">
                <java-doc-item-card v-for="(item, index) in viewData.methodList" :key="item.id || index" v-bind:data="item"></java-doc-item-card>
            </template>
            <div v-else class="jdoc-empty">
                <el-icon><Collection /></el-icon>
                <div class="jdoc-empty__title">请从左侧依次选择 工程 → 包 → 类</div>
                <div class="jdoc-empty__desc">选中类之后，这里会展示该类的注释说明与方法列表</div>
            </div>
        </section>
    </div>
</template>

<script>
import JavaDocItemCard from './JavaDocItemCard';
import request from '../../../utils/request.js';
export default {
    data() {
        return {
            projectList: [],
            navOpen: false,
            fullWidth: document.documentElement.clientWidth,
            viewData: {
                currentProjectId: '',
                currentVersion: '',
                currentPackageName: '',
                currentClassId: '',
                packageList: [],
                classList: [],
                methodList: []
            }
        };
    },
    components: { JavaDocItemCard },
    computed: {
        // 窄屏（手机等小尺寸设备）下导航改为可折叠面板
        isMobile() {
            return this.fullWidth <= 768;
        },
        // 折叠开关上展示当前已选中的层级，便于判断导航状态
        currentPathText() {
            const currentClass = this.viewData.classList.find(item => item.id === this.viewData.currentClassId);
            if (currentClass && currentClass.name) return currentClass.name;
            if (this.viewData.currentPackageName) return this.viewData.currentPackageName;
            const currentProject = this.projectList.find(item => item.id === this.viewData.currentProjectId);
            return currentProject ? currentProject.name : '选择工程 / 包 / 类';
        }
    },
    mounted() {
        this.getProjectList();
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        handleResize() {
            this.fullWidth = document.documentElement.clientWidth;
            // 切回宽屏时收起折叠状态，避免残留
            if (!this.isMobile) {
                this.navOpen = false;
            }
        },
        getProjectList() {
            request({
                url: '/openapi/javadoc/projectList',
                method: 'post',
                data: { p: 'n' }
            }).then(res => {
                if (res.code == 0) {
                    this.projectList = res.data || [];
                }
            });
        },
        getPackageList(projectId, version) {
            this.viewData.packageList = [];
            this.viewData.classList = [];
            this.viewData.methodList = [];
            this.viewData.currentPackageName = '';
            this.viewData.currentClassId = '';

            this.viewData.currentProjectId = projectId;
            this.viewData.currentVersion = version;
            request({
                url: '/openapi/javadoc/packageList',
                method: 'post',
                data: {
                    projectId: this.viewData.currentProjectId,
                    version: this.viewData.currentVersion
                }
            }).then(res => {
                if (res.code == 0) {
                    this.viewData.packageList = res.data || [];
                }
            });
        },
        getClassList(packageName) {
            this.viewData.classList = [];
            this.viewData.methodList = [];
            this.viewData.currentClassId = '';

            this.viewData.currentPackageName = packageName;
            request({
                url: '/openapi/javadoc/classList',
                method: 'post',
                data: {
                    projectId: this.viewData.currentProjectId,
                    version: this.viewData.currentVersion,
                    packageName: this.viewData.currentPackageName
                }
            }).then(res => {
                if (res.code == 0) {
                    this.viewData.classList = res.data || [];
                }
            });
        },
        getClassDetail(classId) {
            this.viewData.currentClassId = classId;
            request({
                url: '/openapi/javadoc/classDetail',
                method: 'post',
                data: {
                    projectId: this.viewData.currentProjectId,
                    classId: this.viewData.currentClassId,
                    version: this.viewData.currentVersion
                }
            }).then(res => {
                if (res.code == 0) {
                    this.viewData.methodList = [];
                    this.viewData.methodList.push(res.meta.classInfo);
                    if (res.meta.methodList) this.viewData.methodList.push(...res.meta.methodList);
                    // 移动端选完类后自动收起导航，直接看内容
                    if (this.isMobile) this.navOpen = false;
                }
            });
        }
    }
};
</script>

<style>
.jdoc-browse {
    display: flex;
    gap: 12px;
    height: 100%;
}

/* 移动端才出现的导航开关 */
.jdoc-browse__toggle {
    display: none;
}

.jdoc-browse__toggletext {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-align: left;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ---------- 左侧三级导航 ---------- */
.jdoc-browse__nav {
    display: flex;
    flex: none;
    flex-direction: column;
    width: 260px;
    padding: 8px;
}

/* 三段等高，各自独立滚动，避免长列表把其它层级挤出视野 */
.jdoc-browse__group {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 0;
}

.jdoc-browse__group + .jdoc-browse__group {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px dashed var(--jdoc-line);
}

.jdoc-browse__grouptitle {
    display: flex;
    flex: none;
    align-items: center;
    gap: 6px;
    padding: 2px 8px 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--jdoc-muted);
}

.jdoc-browse__grouptitle .el-icon {
    color: var(--el-color-primary);
}

.jdoc-browse__list {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
}

/* 导航内的空状态：紧凑一些，避免占满整块列表区 */
.jdoc-browse__list .el-empty {
    padding: 16px 0;
}

/* ---------- 右侧内容 ---------- */
.jdoc-browse__main {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    padding-right: 4px;
    overflow: auto;
}

/* ---------- 小尺寸设备适配 ---------- */
@media (max-width: 768px) {
    .jdoc-browse {
        flex-direction: column;
        gap: 10px;
        height: auto;
    }

    .jdoc-browse__toggle {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        height: 38px;
        padding: 0 12px;
        border: 1px solid var(--jdoc-line);
        border-radius: 10px;
        font-family: inherit;
        font-size: 13px;
        color: var(--jdoc-sub);
        background-color: #fff;
        cursor: pointer;
    }

    /* 折叠态：仅在点击开关后展开三级导航 */
    .jdoc-browse .jdoc-browse__nav {
        display: none;
    }

    .jdoc-browse .jdoc-browse__nav.is-open {
        display: flex;
        width: auto;
    }

    .jdoc-browse__group {
        flex: 0 0 auto;
    }

    .jdoc-browse__list {
        max-height: 200px;
    }

    /* 整页随外层滚动，内容区不再单独滚动 */
    .jdoc-browse__main {
        overflow: visible;
        padding-right: 0;
    }
}
</style>
